import './check-item-prepared-documents.scss'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  Stack,
  IconButton,
  Typography,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  FileUpload,
} from '@vedmarket/ui-kit'
import {
  APPLICATION_DOCUMENT_ACCEPTED_FILES,
  COLORS,
} from 'config/constants'

import ApplicationDocumentLink from './application-document-link'

const formatFileSize = (bytes: number | null | undefined) => {
  if (!bytes || bytes <= 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const CheckItemPreparedDocuments = ({
  documents,
  managerView = false,
  readOnly = false,
  layout = 'default',
  instantUpload = false,
  runAction,
  onUpload,
  onDelete,
}: ApplicationCheckItemPreparedDocumentsProps) => {
  const { t } = useTranslation()
  const handleFile = async (file: File) => {
    if (!onUpload || !runAction) return false
    await runAction(() => onUpload(file), 'applications__message__documentUploaded')
    return false
  }

  const isClosingEmbedded = layout === 'closingEmbedded'
  const isClosingStandalone = layout === 'closing'
  const titleKey = isClosingEmbedded || isClosingStandalone
    ? 'applications__text__blockClosingDocuments'
    : 'applications__text__blockDocuments'

  return (
    <Box
      className={[
        'application-check-item-prepared-documents',
        isClosingStandalone ? 'application-check-item-prepared-documents--closing' : '',
        isClosingEmbedded ? 'application-check-item-prepared-documents--closing-embedded' : '',
      ].filter(Boolean).join(' ')}
    >
      <Stack spacing={1}>
        <Typography variant='body2' sx={{ fontWeight: 600, color: COLORS.BLACK_DARK }}>
          {t(titleKey)}
        </Typography>
        {!readOnly && (
          <Typography variant='caption' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY, display: 'block' }}>
            {instantUpload
              ? t('applications__text__blockDocumentsInstantUploadHint')
              : t('applications__text__blockDocumentsHint')}
          </Typography>
        )}

        {documents.length > 0 ? (
          <ul className='application-check-item-prepared-documents__block-documents-list'>
            {documents.map((doc) => (
              <li key={doc.id} className='application-check-item-prepared-documents__block-documents-item'>
                <ApplicationDocumentLink document={doc} />
                {doc.filesize ? (
                  <Typography component='span' variant='caption' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
                    {formatFileSize(doc.filesize)}
                  </Typography>
                ) : null}
                {!readOnly && onDelete && runAction && (
                  <IconButton
                    size='small'
                    aria-label={t('applications__text__blockDocumentRemove')}
                    onClick={() => runAction(() => onDelete(doc.id), 'applications__message__documentDeleted')}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Typography variant='body2' sx={{ color: managerView ? 'text.primary' : COLORS.LIGHT_TEXT_SECONDARY }}>
            {readOnly
              ? t('applications__text__blockDocumentsEmptyClient')
              : t('applications__text__blockDocumentsEmptyManager')}
          </Typography>
        )}
      </Stack>

      {!readOnly && onUpload && runAction && (
        <FileUpload
          acceptedFilesConfig={APPLICATION_DOCUMENT_ACCEPTED_FILES}
          onHandleFile={handleFile}
        />
      )}
    </Box>
  )
}

export default CheckItemPreparedDocuments
